import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Spin, Select, Empty, Alert, Button, Space, Modal, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { api } from '@/api';
import ClassicBracketView from './ClassicBracketView';
import PdfStyleBracketView from './PdfStyleBracketView';
import { RoundRobinBracket } from './RoundRobinBracket';
import { Participant, TournamentCategory } from '@/types/api.types';
import TournamentService from '@/services/tournament.service';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import { sortTournamentCategories } from '@/utils/tournamentCategorySort';

const { Title } = Typography;
const { Option } = Select;

const TournamentBracketContainer: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Матчи загружаются, но не используются напрямую в этом компоненте
    // const [matches, setMatches] = useState<Match[]>([]);
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [categories, setCategories] = useState<TournamentCategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [tournamentName, setTournamentName] = useState<string>("");
    const [tournamentDate, setTournamentDate] = useState<string | undefined>(undefined);
    const [tournament, setTournament] = useState<any>(null);
    const [isRegenerateModalVisible, setIsRegenerateModalVisible] = useState(false);
    const [isResetModalVisible, setIsResetModalVisible] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [exportingAll, setExportingAll] = useState(false);
    const [exportingAllByTatami, setExportingAllByTatami] = useState(false);
    const [publishingBrackets, setPublishingBrackets] = useState(false);
    /** Вариант отображения сетки: classic — классическая (библиотека), pdf — как в PDF */
    const [bracketVariant, setBracketVariant] = useState<'classic' | 'pdf'>('classic');
    // Участники выбранной категории — загружаем по tc.id через API, чтобы не смешивать с другой категорией (тот же вес).
    const [categoryParticipantsList, setCategoryParticipantsList] = useState<Participant[]>([]);
    const [loadingCategoryParticipants, setLoadingCategoryParticipants] = useState(false);

    const fetchTournamentData = async () => {
        if (!id) return;

        setLoading(true);
        setError(null);

        try {
            // Получаем информацию о турнире
            const tournamentResponse = await api.get(`/tournaments/${id}`);
            const tournamentData = tournamentResponse.data.data || tournamentResponse.data;
            setTournament(tournamentData);
            setTournamentName(tournamentData.name || "Турнир");
            setTournamentDate(tournamentData.tournamentDate);

            // Получаем категории турнира
            const categoriesResponse = await api.get(`/tournaments/${id}/categories`);
            const categoriesData = categoriesResponse.data.data || categoriesResponse.data;
            setCategories(categoriesData);

            // Восстанавливаем выбранную категорию из sessionStorage или выбираем первую
            const savedCategoryId = sessionStorage.getItem(`tournament_${id}_selectedCategory`);
            if (savedCategoryId && categoriesData && categoriesData.some((cat: TournamentCategory) => cat.id === Number(savedCategoryId))) {
                setSelectedCategory(Number(savedCategoryId));
            } else if (categoriesData && categoriesData.length > 0) {
                setSelectedCategory(categoriesData[0].id);
            }

            // Получаем всех участников турнира
            try {
                // Попробуем получить всех участников без фильтрации по категории
                const participantsResponse = await api.get(`/tournaments/${id}/participants`);
                const participantsData = participantsResponse.data.data || participantsResponse.data;
                setParticipants(participantsData);
            } catch (participantsError: any) {
                // Продолжаем работу даже если не удалось получить участников
            }

            // Получаем все матчи турнира (загружаем, но не используем напрямую в этом компоненте)
            // const matchesResponse = await api.get(`/tournaments/${id}/matches`);
            // const matchesData = matchesResponse.data.data || matchesResponse.data;
            // setMatches(matchesData);
        } catch (err: any) {

            // More specific error messages
            if (err.response?.status === 404) {
                setError("Турнир не найден");
            } else if (err.response?.status === 403) {
                setError("Нет доступа к данным турнира");
            } else {
                setError("Ошибка при загрузке данных турнира");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTournamentData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Отслеживаем изменения в sessionStorage при переключении вкладок
    useEffect(() => {
        const handleStorageChange = () => {
            if (!id) return;
            const savedCategoryId = sessionStorage.getItem(`tournament_${id}_selectedCategory`);
            if (savedCategoryId) {
                const categoryId = Number(savedCategoryId);
                // Проверяем, что категория существует в списке категорий
                if (categories.some((cat: TournamentCategory) => cat.id === categoryId)) {
                    setSelectedCategory(categoryId);
                }
            }
        };

        // Проверяем при монтировании компонента
        handleStorageChange();

        // Слушаем события изменения storage (если они происходят в других вкладках)
        window.addEventListener('storage', handleStorageChange);

        // Также проверяем периодически (для случая, когда изменение происходит в той же вкладке)
        const interval = setInterval(handleStorageChange, 100);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, [id, categories]);

    // Загружаем участников выбранной категории по tc.id (API возвращает только эту категорию — без смешивания по весу).
    useEffect(() => {
        if (!id || !selectedCategory) {
            setCategoryParticipantsList([]);
            return;
        }
        let cancelled = false;
        setLoadingCategoryParticipants(true);
        api.get(`/tournaments/${id}/categories/${selectedCategory}/participants`)
            .then((res) => {
                if (cancelled) return;
                const raw = res.data?.data ?? res.data ?? [];
                const list = Array.isArray(raw) ? raw : [];
                const normalized: Participant[] = list.map((p: any) => {
                    const part = p.participant ?? p;
                    const categoryId = p.categoryId ?? selectedCategory ?? null;
                    return {
                        ...p,
                        id: part.id ?? p.id ?? p.participantId ?? p.userId,
                        userId: part.id ?? p.userId ?? p.participantId,
                        participantId: p.participantId ?? p.userId ?? part.id,
                        firstName: part.firstName ?? p.firstName ?? (p as any).first_name ?? '',
                        lastName: part.lastName ?? p.lastName ?? (p as any).last_name ?? '',
                        middleName: part.middleName ?? p.middleName ?? (p as any).middle_name ?? '',
                        clubName: part.clubName ?? (p as any).clubName ?? (p as any).club?.name ?? '',
                        region: part.region ?? (p as any).region ?? (p as any).club?.region ?? '',
                        categoryId,
                    };
                });
                setCategoryParticipantsList(normalized);
            })
            .catch((err) => {
                if (!cancelled) {
                    logger.error('Failed to load category participants', { tournamentId: id, categoryId: selectedCategory, err });
                    setCategoryParticipantsList([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingCategoryParticipants(false);
            });
        return () => { cancelled = true; };
    }, [id, selectedCategory]);

    // Обработчик изменения выбранной категории
    const handleCategoryChange = (value: number) => {
        setSelectedCategory(value);
        // Сохраняем выбранную категорию в sessionStorage
        if (id) {
            sessionStorage.setItem(`tournament_${id}_selectedCategory`, String(value));
        }
    };

    // Обработчик переформирования сетки для выбранной категории
    const handleRegenerateCategory = async () => {
        if (!selectedCategory || !id) return;

        try {
            setRegenerating(true);
            await TournamentService.generateBrackets(Number(id), 1, selectedCategory);
            message.success('Сетка успешно переформирована для выбранной категории');
            setIsRegenerateModalVisible(false);
            // Обновляем только данные категорий/участников без перезагрузки страницы
            await fetchTournamentData();
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Ошибка при переформировании сетки');
        } finally {
            setRegenerating(false);
        }
    };

    // Обработчик сброса сетки для выбранной категории
    const handleResetCategory = async () => {
        if (!selectedCategory || !id) return;

        try {
            setResetting(true);
            await api.delete(`/tournaments/${id}/categories/${selectedCategory}/matches`);
            message.success('Сетка успешно сброшена для выбранной категории');
            setIsResetModalVisible(false);
            await fetchTournamentData();
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Ошибка при сбросе сетки');
        } finally {
            setResetting(false);
        }
    };

    // Функция переноса категории на другой татами

    // Проверяем, опубликованы ли сетки
    const bracketsPublished = tournament?.bracketsPublished ?? false;
    
    // Проверяем, может ли пользователь переформировать сетку
    // Админ, организатор турнира или менеджер/тренер клуба-организатора
    const isAdmin = user?.role === 'admin';
    const isOrganizer = tournament && user && tournament.organizerId === user.id;
    const isManagerOrCoach = user && (user.role === 'manager' || user.role === 'head_manager' || user.role === 'coach');
    const isTournamentClubMember = tournament && user && tournament.clubId && user.clubId && tournament.clubId === user.clubId;
    const canRegenerateBracket = user && (isAdmin || isOrganizer || (isManagerOrCoach && isTournamentClubMember));
    
    // Проверяем, может ли пользователь видеть сетки (до публикации)
    // Менеджеры клуба-организатора должны видеть сетки даже до публикации
    const canViewBrackets = isAdmin || isOrganizer || (isManagerOrCoach && isTournamentClubMember) || bracketsPublished;
    
    // Проверяем, является ли пользователь менеджером клуба
    const isClubManager = user && (user.role === 'manager' && user.clubId);
    
    // Функция публикации сеток
    const handlePublishBrackets = async () => {
        if (!id) return;
        
        try {
            setPublishingBrackets(true);
            await TournamentService.publishBrackets(Number(id));
            message.success('Сетки успешно опубликованы. PDF генерируется в фоновом режиме.');
            
            // Обновляем данные турнира
            const tournamentResponse = await api.get(`/tournaments/${id}`);
            setTournament(tournamentResponse.data.data || tournamentResponse.data);
        } catch (error: any) {
            logger.error('Error publishing brackets:', error);
            message.error(error?.response?.data?.message || 'Ошибка при публикации сеток');
        } finally {
            setPublishingBrackets(false);
        }
    };

    
    // Получаем категории, где зарегистрирован клуб пользователя
    const clubCategories = useMemo(() => {
        if (!user?.clubId || !participants.length) return [];
        
        const clubParticipantCategoryIds = new Set<number>();
        participants.forEach(p => {
            const participantClubId = p.clubId || (p as any).clubId || (p as any).user?.clubId;
            if (participantClubId === user.clubId) {
                const categoryId = p.categoryId || (p as any).categoryId || (p as any).category?.id;
                if (categoryId) {
                    clubParticipantCategoryIds.add(categoryId);
                }
            }
        });
        
        return categories.filter(cat => clubParticipantCategoryIds.has(cat.id));
    }, [user?.clubId, participants, categories]);

    // Экспорт сеток клуба: один метод — список категорий → один PDF
    const exportAllClubBrackets = async () => {
        if (!id || !user?.clubId || clubCategories.length === 0) {
            message.warning('Нет категорий для экспорта');
            return;
        }

        try {
            setExportingAll(true);
            message.loading({ content: `Экспорт ${clubCategories.length} сеток...`, key: 'export-all', duration: 0 });

            const categoryIds = clubCategories.map(c => c.id);
            const fileName = `${(tournamentName || 'турнир').replace(/[<>:"/\\|?*]/g, '_')}_сетки_клуба.pdf`;
            await TournamentService.downloadBracketsPdf(Number(id), categoryIds, fileName);

            message.success({ content: `Экспортировано ${clubCategories.length} сеток`, key: 'export-all' });
        } catch (error) {
            logger.error('Export club brackets error:', error);
            message.error({ content: 'Ошибка при экспорте сеток', key: 'export-all' });
        } finally {
            setExportingAll(false);
        }
    };
    
    // Функция экспорта всех веток по всем татами (только для менеджеров клуба)
    // Разбиваем на отдельные файлы по татами, чтобы избежать ошибки "Invalid string length"
    const exportAllBracketsByTatami = async () => {
        if (!id) {
            message.warning('ID турнира не найден');
            return;
        }
        
        try {
            setExportingAllByTatami(true);
            message.loading({ content: 'Поиск готового PDF документа...', key: 'export-all-tatami', duration: 0 });
            
            // Получаем все файлы турнира
            const filesResponse = await api.get(`/tournament-files/tournament/${id}`);
            const files = filesResponse.data?.data || filesResponse.data || [];
            
            // Ищем PDF файл, сгенерированный при публикации сеток
            const pdfFile = files.find((file: any) => 
                file.fileType === 'application/pdf' && 
                (file.fileName?.includes('все_категории') || file.fileName?.includes('все категории'))
            );
            
            if (!pdfFile) {
                message.warning({ 
                    content: 'PDF документ не найден. Убедитесь, что сетки были опубликованы.', 
                    key: 'export-all-tatami',
                    duration: 5
                });
                return;
            }
            
            // Загружаем готовый PDF файл
            message.loading({ content: 'Загрузка PDF документа...', key: 'export-all-tatami', duration: 0 });
            
            const { TournamentFileService } = await import('@/services/tournament-file.service');
            await TournamentFileService.download(pdfFile.id, pdfFile.fileName || 'все_сетки.pdf');
            
            message.success({ 
                content: 'PDF документ успешно загружен', 
                key: 'export-all-tatami',
                duration: 3
            });
        } catch (error) {
            logger.error('Ошибка при загрузке PDF документа:', error);
            message.error({ 
                content: `Ошибка при загрузке: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`, 
                key: 'export-all-tatami',
                duration: 5
            });
        } finally {
            setExportingAllByTatami(false);
        }
    };


    if (!id) {
        return <div>ID турнира не найден</div>;
    }

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" />
                <div style={{ marginTop: "20px" }}>Загрузка данных турнира...</div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="Ошибка"
                description={error}
                type="error"
                showIcon
                style={{ maxWidth: "600px", margin: "50px auto" }}
            />
        );
    }

    return (
        <div style={{ padding: "20px" }}>
            <Title level={2}>{tournamentName} - Турнирная сетка</Title>

            <div style={{ marginBottom: "20px" }}>
                <Space size="middle" wrap>
                    <Select
                        placeholder="Выберите категорию"
                        style={{ minWidth: 200, maxWidth: '100%', width: 'auto' }}
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        dropdownStyle={{ maxWidth: 600, minWidth: 300 }}
                        optionLabelProp="label"
                        popupMatchSelectWidth={false}
                    >
                        {sortTournamentCategories(categories).map((category) => (
                            <Option 
                                key={category.id} 
                                value={category.id}
                                label={category.name}
                                title={category.name}
                            >
                                <div style={{ 
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'normal',
                                    lineHeight: '1.5',
                                    padding: '4px 0'
                                }}>
                                    {category.name}
                                </div>
                            </Option>
                        ))}
                    </Select>
                    <Select
                        value={bracketVariant}
                        onChange={(v: 'classic' | 'pdf') => setBracketVariant(v)}
                        style={{ minWidth: 180 }}
                        options={[
                            { value: 'classic', label: 'Классическая сетка' },
                            { value: 'pdf', label: 'Как в PDF' },
                        ]}
                    />
                    {user?.clubId && clubCategories.length > 0 && (
                        <Button
                            type="default"
                            icon={<DownloadOutlined />}
                            onClick={exportAllClubBrackets}
                            loading={exportingAll}
                        >
                            Выгрузить все сетки клуба ({clubCategories.length})
                        </Button>
                    )}
                    
                    {isClubManager && (
                        <Button
                            type="default"
                            icon={<DownloadOutlined />}
                            onClick={exportAllBracketsByTatami}
                            loading={exportingAllByTatami}
                        >
                            Выгрузить все сетки
                        </Button>
                    )}
                    
                    {canRegenerateBracket && !bracketsPublished && (
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handlePublishBrackets}
                            loading={publishingBrackets}
                        >
                            Сохранить и опубликовать сетки
                        </Button>
                    )}
                    
                </Space>
            </div>

            <Modal
                title="Переформирование сетки категории"
                open={isRegenerateModalVisible}
                onOk={handleRegenerateCategory}
                onCancel={() => setIsRegenerateModalVisible(false)}
                okText="Переформировать"
                cancelText="Отмена"
                confirmLoading={regenerating}
            >
                <Alert 
                    message="Внимание!" 
                    description="Соперники в сетке будут перераспределены заново с учетом клубов, тренеров и федераций. Номера боев останутся прежними. Все результаты матчей будут сброшены."
                    type="warning"
                    showIcon
                />
            </Modal>

            <Modal
                title="Сброс сетки категории"
                open={isResetModalVisible}
                onOk={handleResetCategory}
                onCancel={() => setIsResetModalVisible(false)}
                okText="Сбросить"
                okButtonProps={{ danger: true }}
                cancelText="Отмена"
                confirmLoading={resetting}
            >
                <Alert 
                    message="ВНИМАНИЕ! Только для разработки и тестирования!" 
                    description="Все матчи этой категории будут полностью удалены из базы данных. Это действие необратимо!"
                    type="error"
                    showIcon
                />
            </Modal>


            {!canViewBrackets ? (
                <Empty 
                    description="Сетки еще не опубликованы. Дождитесь публикации менеджером турнира."
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            ) : selectedCategory ? (
                (() => {
                    const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
                    
                    if (!selectedCategoryData) {
                        return <Empty description="Категория не найдена" />;
                    }

                    // Участники загружены по tc.id через API (categoryParticipantsList) — без смешивания с другой категорией по весу.
                    const participantsForBracket = categoryParticipantsList;
                    if (loadingCategoryParticipants) {
                        return (
                            <Spin tip="Загрузка участников категории...">
                                <div style={{ minHeight: 120 }} />
                            </Spin>
                        );
                    }
                    if (participantsForBracket.length === 0) {
                        return (
                            <Empty description="Нет участников в этой категории" />
                        );
                    }
                    if (participantsForBracket.length === 1) {
                        return (
                            <Empty description="Для генерации сетки требуется минимум 2 участника" />
                        );
                    }
                    if (participantsForBracket.length === 3) {
                        return (
                            <RoundRobinBracket
                                tournamentId={Number(id)}
                                category={selectedCategoryData}
                                participants={participantsForBracket}
                            />
                        );
                    }
                    return bracketVariant === 'pdf' ? (
                        <PdfStyleBracketView
                            tournamentId={Number(id)}
                            category={selectedCategoryData}
                            participants={participantsForBracket}
                            tournamentDate={tournamentDate}
                            bracketsPublished={bracketsPublished}
                        />
                    ) : (
                        <ClassicBracketView
                            tournamentId={Number(id)}
                            category={selectedCategoryData}
                            participants={participantsForBracket}
                            tournamentDate={tournamentDate}
                            bracketsPublished={bracketsPublished}
                        />
                    );
                })()
            ) : (
                <Empty description="Выберите категорию для отображения турнирной сетки" />
            )}
        </div>
    );
};

export default TournamentBracketContainer;
